import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { formatDateForApi, getTodayForApi, parseApiDate } from '~/utils/timezone'

function addDaysYmd(ymd: string, days: number): string {
  const d = parseApiDate(ymd)
  if (!d) return ymd
  const t = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  t.setDate(t.getDate() + days)
  return formatDateForApi(t)
}

/**
 * Default for the user-editable "Selected billing period" (YYYY-MM-DD, local calendar).
 *
 * Default window: today → today + 30 days (forward-looking).
 * License period parameters are accepted for API compatibility but do not
 * constrain the default range — dates are fully selectable.
 */
export function computeDefaultSelectedBillingPeriod(
  _periodStart: string | undefined | null,
  _periodEnd: string | undefined | null
): { start: string; end: string } {
  const today = getTodayForApi()
  return { start: today, end: addDaysYmd(today, 30) }
}

/**
 * Custom date range for usage views (team + personal). Defaults from
 * {@link computeDefaultSelectedBillingPeriod}; user overrides may be any span ≥ 1 calendar day.
 */
export function useCustomDateRangeFromLicense(
  billingAccountId: string | number | undefined | null,
  periodStart: string | undefined | null,
  periodEnd: string | undefined | null
): {
  customStartDate: string
  customEndDate: string
  onCustomStartDateChange: (start: string) => void
  onCustomEndDateChange: (end: string) => void
} {
  const [override, setOverride] = useState<{ start: string; end: string } | null>(null)

  useEffect(() => {
    setOverride(null)
  }, [billingAccountId])

  const base = useMemo(
    () => computeDefaultSelectedBillingPeriod(periodStart, periodEnd),
    [periodStart, periodEnd]
  )

  const baseRef = useRef(base)
  baseRef.current = base

  const customStartDate = override?.start ?? base.start
  const customEndDate = override?.end ?? base.end

  const onCustomStartDateChange = useCallback((start: string) => {
    setOverride(prev => ({
      start,
      end: prev?.end ?? baseRef.current.end
    }))
  }, [])

  const onCustomEndDateChange = useCallback((end: string) => {
    setOverride(prev => ({
      start: prev?.start ?? baseRef.current.start,
      end
    }))
  }, [])

  return {
    customStartDate,
    customEndDate,
    onCustomStartDateChange,
    onCustomEndDateChange
  }
}

/**
 * Translate the dashboard's UI state into the API params for `useUsageDashboard` /
 * `getPersonalUsage`.
 *
 *  1. `billing_period` mode always maps to `timeRange='billing_cycle'`. The backend
 *     resolves the window from the license using the exact period-start *instant*
 *     (e.g. `2026-05-12T18:45:24Z`) and reports the quota/enforcement-aligned credit
 *     total. We must NOT down-convert to a date-only `custom` range here: slicing
 *     `licenseStart` to `YYYY-MM-DD` floored the start to local midnight, which (a)
 *     pulled pre-period usage into the window — e.g. a task run minutes before a
 *     mid-cycle upgrade, counted because midnight precedes the real 18:45 start — and
 *     (b) routed credits through the raw event sum instead of the quota anchor, so
 *     "Team Credit Usage" rendered *above* the enforcement number and never matched
 *     the lock/quota path. (Regression from #621; see CUR ticket.)
 *
 *     The original reason #621 switched to `custom` — stopping the bar chart from
 *     drawing empty future buckets for far-future / sentinel (`2099-12-31`) license
 *     ends — is already handled server-side: `_effective_chart_end_for_series` clamps
 *     the chart series to `min(period_end, now)`. So the frontend no longer needs to
 *     cap the range, and doing so only cost us timestamp precision.
 *  2. `date_range` mode always passes the user-picked custom window through as-is.
 *
 * The trigger label is unaffected — users still see "May 1 – Jun 15, 2026" so they
 * know when their plan renews.
 */
export function resolveUsageApiRange(args: {
  mode: TimeRangeMode
  customStart: string | undefined
  customEnd: string | undefined
  licenseStart: string | undefined | null
  licenseEnd: string | undefined | null
}): { timeRange: 'billing_cycle' | 'custom'; start?: string; end?: string } {
  if (args.mode === 'date_range') {
    return { timeRange: 'custom', start: args.customStart, end: args.customEnd }
  }
  return { timeRange: 'billing_cycle' }
}
